import extensions from '../extensions';
import site from '../site';
import availableHooks from './constants/available-hooks';
import extensionHooks from './constants/extension-hooks';
import callHookListeners from './operations/call-hook-listeners';
import prepareSerializedStaticDocument from '../hdrm/utils/helpers/prepare-serialized-static-document';
import { connectedStore } from '../store';

export default {
  onAfterAddNewPage(newPage) {
    extensions.helpers.applyHook(extensionHooks.ON_AFTER_ADD_NEW_PAGE, newPage);
    callHookListeners(availableHooks.ON_AFTER_ADD_NEW_PAGE, newPage);
  },

  onAfterDeletePage(deletedPage) {
    extensions.helpers.applyHook(extensionHooks.ON_AFTER_DELETE_PAGE, deletedPage);
    callHookListeners(availableHooks.ON_AFTER_DELETE_PAGE, deletedPage);
  },

  onAfterChangeHomePage(newHomePage, oldHomePage) {
    extensions.helpers.applyHook(
      extensionHooks.ON_AFTER_CHANGE_HOME_PAGE,
      newHomePage,
      oldHomePage
    );
    callHookListeners(availableHooks.ON_AFTER_CHANGE_HOME_PAGE, newHomePage, oldHomePage);
  },

  onAfterChangePageOrder(pages) {
    extensions.helpers.applyHook(extensionHooks.ON_AFTER_CHANGE_PAGE_ORDER, pages);
    callHookListeners(availableHooks.ON_AFTER_CHANGE_PAGE_ORDER, pages);
  },

  onAfterSiteConnect() {
    callHookListeners(availableHooks.ON_AFTER_SITE_CONNECT);
  },

  onAfterUpdatePageSettings(updatedPage) {
    extensions.helpers.applyHook(extensionHooks.ON_AFTER_UPDATE_PAGE_SETTINGS, updatedPage);
    callHookListeners(availableHooks.ON_AFTER_UPDATE_PAGE_SETTINGS, updatedPage);
  },

  onAfterBlockAdded({ blockId, variationId, targetBlockId, targetBlockVariationId }) {
    callHookListeners(availableHooks.ON_AFTER_BLOCK_ADDED, {
      blockId,
      variationId,
      targetBlockId,
      targetBlockVariationId,
    });
  },

  onAfterBlockDuplicated({
    blockId,
    variationId,
    elementId,
    constraintId,
    targetBlockId,
    targetBlockVariationId,
  }) {
    callHookListeners(availableHooks.ON_AFTER_BLOCK_DUPLICATED, {
      blockId,
      variationId,
      elementId,
      constraintId,
      targetBlockId,
      targetBlockVariationId,
    });
  },

  onBeforeBlockDeleted({ blockId, variationId, elementId, constraintId }) {
    callHookListeners(availableHooks.ON_BEFORE_BLOCK_DELETED, {
      blockId,
      variationId,
      elementId,
      constraintId,
    });
  },

  onAfterBlockDeleted({ blockId, variationId }) {
    callHookListeners(availableHooks.ON_AFTER_BLOCK_DELETED, {
      blockId,
      variationId,
    });
  },

  onAfterBlockShifted({
    blockId,
    variationId,
    previousPosition,
    currentPosition,
    elementId,
    constraintId,
  }) {
    callHookListeners(availableHooks.ON_AFTER_BLOCK_SHIFTED, {
      blockId,
      variationId,
      previousPosition,
      currentPosition,
      elementId,
      constraintId,
    });
  },

  onAfterBlockCopied({ blockId, variationId }) {
    callHookListeners(availableHooks.ON_AFTER_BLOCK_COPIED, {
      blockId,
      variationId,
    });
  },

  onAfterBlockCutout({ blockId, variationId }) {
    callHookListeners(availableHooks.ON_AFTER_BLOCK_CUTOUT, {
      blockId,
      variationId,
    });
  },

  onAfterBlockPasted({ refElementId, position }) {
    callHookListeners(availableHooks.ON_AFTER_BLOCK_PASTED, {
      refElementId,
      position,
    });
  },

  onBeforeBlurFocusedElement(liveElement) {
    extensions.helpers.applyHook(extensionHooks.ON_BEFORE_BLUR_FOCUSED_ELEMENT, liveElement);
    callHookListeners(availableHooks.ON_BEFORE_BLUR_FOCUSED_ELEMENT, liveElement);
  },

  onAfterBlurFocusedElement(liveElement) {
    extensions.helpers.applyHook(extensionHooks.ON_AFTER_BLUR_FOCUSED_ELEMENT, liveElement);
    callHookListeners(availableHooks.ON_AFTER_BLUR_FOCUSED_ELEMENT);
  },

  onBeforeSiteSave() {
    callHookListeners(availableHooks.ON_BEFORE_SITE_SAVE);
  },

  onAfterSiteSave() {
    callHookListeners(availableHooks.ON_AFTER_SITE_SAVE);
  },

  onAfterElementFocused({ elementId }) {
    callHookListeners(availableHooks.ON_AFTER_ELEMENT_FOCUSED, { elementId });
  },

  onAfterElementSelected({ elementId }) {
    callHookListeners(availableHooks.ON_AFTER_ELEMENT_SELECTED, { elementId });
  },

  onAfterDialogHide() {
    callHookListeners(availableHooks.ON_AFTER_DIALOG_HIDE);
  },

  /**
   * HDRM callbacks
   */

  onStaticDocumentConnected(staticDocument) {
    function commitDocumentChanges() {
      const store = connectedStore.get();
      const state = store.getState();
      const pageId = site.selectors.getActivePageId(state);
      const html = prepareSerializedStaticDocument(staticDocument.cloneNode(true));

      store.dispatch(site.actions.setPageHTML(pageId, html));
    }

    callHookListeners(
      availableHooks.ON_STATIC_DOCUMENT_CONNECTED,
      staticDocument,
      commitDocumentChanges
    );
  },

  onLiveDocumentComposed(liveDocument) {
    callHookListeners(availableHooks.ON_LIVE_DOCUMENT_COMPOSED, liveDocument);
  },

  onLiveDocumentConnected(liveDocument) {
    callHookListeners(availableHooks.ON_LIVE_DOCUMENT_CONNECTED, liveDocument);
  },

  onBeforeSynchronizeStaticElement(oldElement, newElement) {
    extensions.helpers.applyHook(
      extensionHooks.ON_BEFORE_SYNCHRONIZE_STATIC_ELEMENT,
      oldElement,
      newElement
    );
    callHookListeners(availableHooks.ON_BEFORE_SYNCHRONIZE_STATIC_ELEMENT, oldElement, newElement);
  },

  onBeforeSynchronizeLiveElement(element) {
    callHookListeners(availableHooks.ON_BEFORE_SYNCHRONIZE_LIVE_ELEMENT, element);
  },

  onBeforeInsertLiveElement(element) {
    callHookListeners(availableHooks.ON_BEFORE_INSERT_LIVE_ELEMENT, element);
  },

  onBeforeSetStaticElementAttribute(staticElement, attribute, value) {
    callHookListeners(
      availableHooks.ON_BEFORE_SET_STATIC_ELEMENT_ATTRIBUTE,
      staticElement,
      attribute,
      value
    );
  },

  onBeforeSetLiveElementAttribute(staticElement, attribute, value) {
    callHookListeners(
      availableHooks.ON_BEFORE_SET_LIVE_ELEMENT_ATTRIBUTE,
      staticElement,
      attribute,
      value
    );
  },

  onBeforeReplaceStaticElement(oldElement, newElement) {
    callHookListeners(availableHooks.ON_BEFORE_REPLACE_STATIC_ELEMENT, oldElement, newElement);
  },

  onBeforeReplaceLiveElement(oldElement, newElement) {
    callHookListeners(availableHooks.ON_BEFORE_REPLACE_LIVE_ELEMENT, oldElement, newElement);
  },

  onBeforeInsertStaticElement(staticElement) {
    callHookListeners(availableHooks.ON_BEFORE_INSERT_STATIC_ELEMENT, staticElement);
  },

  onBeforeDuplicateStaticElement(staticElement) {
    callHookListeners(availableHooks.ON_BEFORE_DUPLICATE_STATIC_ELEMENT, staticElement);
  },

  onAfterDuplicateStaticElement(staticElement) {
    callHookListeners(availableHooks.ON_AFTER_DUPLICATE_STATIC_ELEMENT, staticElement);
  },

  onAfterInsertStaticElement(staticElement) {
    callHookListeners(availableHooks.ON_AFTER_INSERT_STATIC_ELEMENT, staticElement);
  },

  onAfterInsertLiveElement(liveElement) {
    callHookListeners(availableHooks.ON_AFTER_INSERT_LIVE_ELEMENT, liveElement);
  },

  onAfterShiftStaticElement(staticElement) {
    callHookListeners(availableHooks.ON_AFTER_SHIFT_STATIC_ELEMENT, staticElement);
  },

  onAfterReplaceLiveElement(oldLiveElement, newLiveElement) {
    callHookListeners(availableHooks.ON_AFTER_REPLACE_LIVE_ELEMENT, oldLiveElement, newLiveElement);
  },

  onAfterSynchronizeStaticElement(staticElement) {
    callHookListeners(availableHooks.ON_AFTER_SYNCHRONIZE_STATIC_ELEMENT, staticElement);
  },

  /**
   * View hooks
   */

  onBeforePageLoaded(page) {
    callHookListeners(availableHooks.ON_BEFORE_PAGE_LOADED, page);
  },

  onAfterViewScrollerInitialized(liveDocument) {
    callHookListeners(extensionHooks.ON_AFTER_VIEW_SCROLLER_INITIALIZED, liveDocument);
  },

  /**
   * Live document events
   */

  onLiveDocumentCopy(event) {
    callHookListeners(availableHooks.ON_LIVE_DOCUMENT_COPY, event);
  },

  onLiveDocumentPaste(event) {
    callHookListeners(availableHooks.ON_LIVE_DOCUMENT_PASTE, event);
  },

  onTextPaste(event, preventHandler) {
    callHookListeners(availableHooks.ON_TEXT_PASTE, event, preventHandler);
  },

  onTextCut(event) {
    callHookListeners(availableHooks.ON_TEXT_CUT, event);
  },

  onTextCopy(event) {
    callHookListeners(availableHooks.ON_TEXT_COPY, event);
  },

  onSiteLoaded() {
    callHookListeners(availableHooks.ON_SITE_LOADED);
  },

  onBeforeCSSGeneratorInit() {
    callHookListeners(availableHooks.ON_BEFORE_CSS_GENERATOR_INIT);
  },
};
